import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from 'auth/context/AuthContext';
import { DASHBOARD, SUBSCRIPTION_PAGE } from 'utilities/routes';
import { SUBSCRIPTION_ACTIVE_STATUS } from 'auth/context/AuthProvider';
import { ReactComponent as LogoIcon } from 'assets/icons/sgbnet.svg';
import { ProviderAuthenticatedNavbar } from 'components/navbar/ProviderAuthenticatedNavbar';
import { ProviderAuthenticatedNotApprovedNavbar } from 'components/navbar/ProviderAuthenticatedNotApprovedNavbar';
import ExpiringSubscriptionOutlet from 'components/navbar/ExpiringSubscriptionOutlet';
import UpdateLisences from 'components/navbar/UpdateLisences';
import JoinToAppointmentOutlet from 'components/navbar/JoinToAppointmentOutlet';
import ScheduledDnaTestOutlet from 'components/navbar/ScheduledDnaTestOutlet';

interface ProviderNavbarProps {
  openNavbarMobile: () => void;
}

export const ProviderNavbar: React.FC<ProviderNavbarProps> = ({
  openNavbarMobile,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, stripeSubscription, hasActivePeriod } = useAuth();

  const isApproved = Boolean(
    user?.providerEnabled && stripeSubscription?.length && hasActivePeriod,
  );

  const isShowSubExpiredBanner = useMemo(
    () =>
      !!stripeSubscription?.length &&
      !SUBSCRIPTION_ACTIVE_STATUS.includes(
        stripeSubscription[0]?.status as number,
      ) &&
      location.pathname !== SUBSCRIPTION_PAGE,
    [location.pathname, stripeSubscription],
  );

  return (
    <div className="w-full z-[999] relative desktop:min-w-[1300px] box-border bg-white border-b shrink-0 p-0">
      <div className="w-full h-2.5 bg-clc-blue" />
      <div
        className="w-full h-[70px] desktop:h-[90px] px-7 2xl:px-[135px] flex justify-between items-center gap-4"
        data-testid="navbar-provider"
      >
        <LogoIcon
          className="w-[117px] h-[27px] desktop:w-[162px] desktop:h-9 cursor-pointer"
          onClick={() => navigate(DASHBOARD)}
        />

        <div className="ml-auto">
          {isApproved ? (
            <ProviderAuthenticatedNavbar openNavbarMobile={openNavbarMobile} />
          ) : (
            <ProviderAuthenticatedNotApprovedNavbar
              openNavbarMobile={openNavbarMobile}
            />
          )}
        </div>
      </div>

      <div className="w-full hidden desktop:block">
        {isShowSubExpiredBanner && <ExpiringSubscriptionOutlet />}
        <UpdateLisences />
        <JoinToAppointmentOutlet />
        <ScheduledDnaTestOutlet />
      </div>
    </div>
  );
};
